import { useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";

import { IconButton } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import { SIBreadcrumbs } from "app/pages/sales-intelligence/common-components/SIBreadcrumbs/SIBreadcrumbs";
import { SignalsManagementSection } from "app/pages/sales-intelligence/common-components/SignalsManagementSection/SignalsManagementSection";
import { MANAGEMENT_APIS_AND_WEBHOOKS_ROUTE } from "app/pages/sales-intelligence/constants/sl_routes";
import managementTrackingService from "app/pages/sales-intelligence/modules/management/services/tracking/managementTrackingService";
import { resolveSignalsApiCustomAttributes } from "app/pages/sales-intelligence/modules/management/services/tracking/utils";
import manageIntegrationsTrackingService from "app/pages/sales-intelligence/modules/manage-integrations/services/tracking";
import { SIGNALS_API_DOCS_URL } from "app/pages/sales-intelligence/modules/management/sub-modules/constants";
import { SignalsApiEmptyView } from "app/pages/sales-intelligence/modules/management/sub-modules/signals-api/components/SignalsApiEmptyView/SignalsApiEmptyView";
import { SignalsApiListTypesContainer } from "app/pages/sales-intelligence/modules/management/sub-modules/signals-api/containers/SignalsApiListTypesContainer/SignalsApiListTypesContainer";
import { useSignalsApiStatusPollingQuery } from "app/pages/sales-intelligence/modules/management/sub-modules/signals-api/query/queries/useSignalsApiStatusPollingQuery";
import { DomainManagementButtonContainer } from "app/pages/sales-intelligence/modules/management/sub-modules/subscribe-domains/containers/DomainManagementButtonContainer/DomainManagementButtonContainer";
import { SubscribeDomainsContent } from "app/pages/sales-intelligence/modules/management/sub-modules/subscribe-domains/containers/SubscribeDomainsContent/SubscribeDomainsContent";
import { SubscribeDomainsEmptyView } from "app/pages/sales-intelligence/modules/management/sub-modules/subscribe-domains/containers/SubscribeDomainsEmptyView/SubscribeDomainsEmptyView";
import SignalsApiWebhookContainer from "app/pages/sales-intelligence/modules/signals-api-webhook/containers/SignalsApiWebhookContainer";
import { managementIntegrationSignalApiStorage } from "app/pages/sales-intelligence/pages/manage-integration/services/storage/storage";
import ManagementSignalApiModal from "app/pages/sales-intelligence/pages/management/components/ManagementSignalApiModal/ManagementSignalApiModal";
import siNavigator from "app/pages/sales-intelligence/services/siNavigator";
import { EducationBarContainer, useEducationBarState } from "app/pages/sales-intelligence/sub-modules/education-bar";
import SignalsManagementButtonContainer from "app/pages/sales-intelligence/sub-modules/signals/containers/SignalsManagementButton";
import withSignalsManagement from "app/pages/sales-intelligence/sub-modules/signals/hoc/withSignalsManagement";
import { usePermanentSignalsAmountBySource } from "app/pages/sales-intelligence/sub-modules/signals/hooks/useUISignals";
import SignalSource from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalSource";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import { SIGNALS_API_BREADCRUMBS } from "../../constants/breadcrumbs";

import {
    StyledBreadcrumbsContainer,
    StyledHeader,
    StyledHelpButton,
    StyledManagementHeader,
    StyledManagementTabsContainer,
    StyledManagementTitle,
    StyledManagementWrapper,
} from "./styles";

const SignalsManagementIntegrationContainer = () => {
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const { state } = useCurrentStateAndParams();
    const { data } = useSignalsApiStatusPollingQuery();

    const { isBarVisible, onClose, onOpen } = useEducationBarState(managementIntegrationSignalApiStorage);

    const allItemsDone =
        data?.subscribedDomainsCount &&
        data?.subscribedDomainsCount > 0 &&
        data?.signalsCount > 0 &&
        data?.isWebhooksConfigured;

    const hasSubscribedDomains =
        (data?.subscribedDomainsCount && data?.subscribedDomainsCount > 0) || data?.isDomainsFileProcessing;

    const handleOnManagementClick: VoidFunction = () => {
        managementTrackingService.trackSignalApiClickEvent("manage signals", resolveSignalsApiCustomAttributes(data));
    };

    const signalsAmount = usePermanentSignalsAmountBySource();

    const handleOnSubmit = () => {
        managementTrackingService.trackSignalApiClickEvent(
            "are you sure/submit",
            resolveSignalsApiCustomAttributes(data),
        );
        siNavigator.goToAPIsWebhooksPage();
    };
    const handleOnCancel = () => {
        managementTrackingService.trackSignalApiClickEvent(
            "are you sure/cancel",
            resolveSignalsApiCustomAttributes(data),
        );
        setIsOpenModal(false);
    };

    const handleEmptyViewClick: VoidFunction = () => {
        managementTrackingService.trackSignalApiClickEvent("manage signals", resolveSignalsApiCustomAttributes(data));
    };

    const handleBreadcrumbClick = ({ state }: { state: string }): void => {
        if (state === MANAGEMENT_APIS_AND_WEBHOOKS_ROUTE) {
            if (allItemsDone) {
                handleOnSubmit();
            } else {
                setIsOpenModal(true);
            }

            return;
        }

        void proNavigator.go(state);
    };

    const breadcrumbs = SIGNALS_API_BREADCRUMBS.map((item) => ({ ...item, title: translate(item.title) }));

    const handleGuideClick = (): void => {
        manageIntegrationsTrackingService.trackSignalsWebhookGuideClick();
    };

    return (
        <StyledManagementWrapper data-automation="signals-api-webhook-management-page">
            {isBarVisible ? (
                <EducationBarContainer
                    onClose={onClose}
                    stateId={state.stateId || ""}
                />
            ) : null}

            <StyledBreadcrumbsContainer>
                <SIBreadcrumbs
                    variant="spaceBetween"
                    list={breadcrumbs}
                    onClick={handleBreadcrumbClick}
                    dataAutomation="signals-manage-breadcrumbs"
                >
                    <a
                        href={SIGNALS_API_DOCS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <IconButton
                            iconName="helpcenter"
                            type="flat"
                            data-automation="signals-webhook-management.guide-button"
                            onClick={handleGuideClick}
                        />
                    </a>
                </SIBreadcrumbs>
            </StyledBreadcrumbsContainer>

            <StyledManagementTabsContainer>
                <StyledHeader>
                    <ManagementSignalApiModal
                        isOpened={isOpenModal}
                        onSubmit={handleOnSubmit}
                        onCancel={handleOnCancel}
                    />

                    <StyledManagementHeader>
                        <I18n
                            element={StyledManagementTitle}
                            translatable={{ key: "si.management.integration.signals-api.page.title" }}
                        />
                        {!isBarVisible ? <StyledHelpButton onClick={onOpen} /> : null}
                    </StyledManagementHeader>
                </StyledHeader>
                <SignalsManagementSection
                    title={translate("si.management.integration.signals-api.section.set-signals")}
                    subTitle={translate("si.management.integration.signals-api.section.set-signals.subtitle")}
                    actionButton={
                        signalsAmount > 0 ? (
                            <SignalsManagementButtonContainer
                                fillButtonText="si.management.integration.signals-api.section.set-signals.manage-signals"
                                onClick={handleOnManagementClick}
                            />
                        ) : null
                    }
                >
                    {signalsAmount > 0 ? (
                        <SignalsApiListTypesContainer />
                    ) : (
                        <SignalsApiEmptyView onClick={handleEmptyViewClick} />
                    )}
                </SignalsManagementSection>
                <SignalsManagementSection
                    title={translate("si.management.integration.signals-api.section.subscribe-domains")}
                    subTitle={translate("si.management.integration.signals-api.section.subscribe-domains.subtitle")}
                    actionButton={
                        hasSubscribedDomains ? (
                            <DomainManagementButtonContainer isDisabled={data.isDomainsFileProcessing} />
                        ) : null
                    }
                >
                    {hasSubscribedDomains ? <SubscribeDomainsContent /> : <SubscribeDomainsEmptyView data={data} />}
                </SignalsManagementSection>
                <SignalsManagementSection
                    title={translate("si.management.integration.signals-api.section.webhook")}
                    subTitle={translate("si.management.integration.signals-api.section.webhook.subtitle")}
                >
                    <SignalsApiWebhookContainer />
                </SignalsManagementSection>
            </StyledManagementTabsContainer>
        </StyledManagementWrapper>
    );
};

export default withSignalsManagement(SignalsManagementIntegrationContainer, SignalSource.Api);
